import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { portal as portalApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PortalActionItem from '../components/PortalActionItem';
import MyTrails from '../components/MyTrails';
import Markdown from '../components/Markdown';
import { formatDate, formatTime } from '../utils/portal';

// Shared Google Drive folder for committee documents — letterhead, outreach
// letters, vendor scopes. Referenced repeatedly in the minutes.
const COMMITTEE_FILES_URL =
  'https://drive.google.com/drive/folders/1ZUMXUkdSO__CDgvxRSny8FgMEp8GiW8M?usp=sharing';

/**
 * Volunteer Portal home — the standing answer to "what's next and what do I owe?"
 */
export default function PortalPage() {
  const { isAdmin, isChair } = useAuth();
  const navigate = useNavigate();
  const canManage = isAdmin || isChair;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await portalApi.getOverview());
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The committee meets the 2nd Tuesday; scheduling the next one is one click.
  const scheduleNext = async () => {
    setCreating(true);
    try {
      const meeting = await portalApi.createMeeting({ meeting_date: data.next_scheduled_date });
      navigate(`/volunteer/meetings/${meeting.id}`);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
      setCreating(false);
    }
  };

  if (loading) return <div className="loading">Loading portal…</div>;
  if (!data) return <div className="portal-page"><div className="alert alert-error">{message}</div></div>;

  const { upcoming_meeting: upcoming, last_meeting: last } = data;
  const nextIsScheduled = upcoming && upcoming.meeting_date === data.next_scheduled_date;

  return (
    <div className="portal-page">
      <div className="portal-header">
        <div>
          <h1>Volunteer Portal</h1>
          <p className="portal-subtitle">
            Agendas, minutes, and who's doing what. The committee meets the
            <strong> 2nd Tuesday</strong> of each month.
          </p>
        </div>
        <div className="portal-header-links">
          <a
            href={COMMITTEE_FILES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-sm btn-secondary"
          >
            📁 Committee Files
          </a>
          <Link to="/volunteer/meetings" className="btn-sm btn-secondary">All Meetings</Link>
          <Link to="/volunteer/actions" className="btn-sm btn-secondary">All Action Items</Link>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      <div className="portal-stats">
        <div className="portal-stat">
          <span className="portal-stat-value">{data.open_count}</span>
          <span className="portal-stat-label">open action items</span>
        </div>
        <div className="portal-stat">
          <span className="portal-stat-value">{data.my_actions.length}</span>
          <span className="portal-stat-label">assigned to you</span>
        </div>
        <div className={`portal-stat ${data.overdue_actions.length ? 'portal-stat-alert' : ''}`}>
          <span className="portal-stat-value">{data.overdue_actions.length}</span>
          <span className="portal-stat-label">overdue</span>
        </div>
        <div className="portal-stat">
          <span className="portal-stat-value">{data.done_count}</span>
          <span className="portal-stat-label">completed</span>
        </div>
      </div>

      {/* ── Next meeting ─────────────────────────────────────────── */}
      <section className="portal-section">
        <h2>Next Meeting</h2>

        {upcoming ? (
          <div className="portal-meeting-card">
            <div className="portal-meeting-card-head">
              <div>
                <Link to={`/volunteer/meetings/${upcoming.id}`} className="portal-meeting-title">
                  {upcoming.title}
                </Link>
                <div className="portal-meeting-when">
                  {formatDate(upcoming.meeting_date)}
                  {upcoming.start_time && <> · {formatTime(upcoming.start_time)}</>}
                  {upcoming.location && <> · {upcoming.location}</>}
                </div>
              </div>
              {upcoming.is_exception && (
                <span className="portal-badge badge-exception" title={upcoming.exception_note || 'Not the 2nd Tuesday'}>
                  Off-cycle
                </span>
              )}
            </div>

            {upcoming.is_exception && upcoming.exception_note && (
              <p className="portal-exception-note">{upcoming.exception_note}</p>
            )}

            {upcoming.agenda_md ? (
              <p className="portal-agenda-status">
                <span>
                  Agenda ready
                  {!upcoming.agenda_published && <span className="portal-badge badge-draft">Draft</span>}
                </span>
                <Link to={`/volunteer/meetings/${upcoming.id}`}>View agenda →</Link>
              </p>
            ) : (
              <p className="portal-muted">
                No agenda yet.{canManage && <> <Link to={`/volunteer/meetings/${upcoming.id}`}>Write one →</Link></>}
              </p>
            )}
          </div>
        ) : (
          <div className="portal-empty">
            <p>
              Nothing on the calendar. The next 2nd Tuesday is{' '}
              <strong>{formatDate(data.next_scheduled_date)}</strong>.
            </p>
            {canManage && (
              <button className="btn-sm btn-primary" onClick={scheduleNext} disabled={creating}>
                {creating ? 'Scheduling…' : `Schedule ${formatDate(data.next_scheduled_date, { month: 'long', day: 'numeric' })}`}
              </button>
            )}
          </div>
        )}

        {canManage && upcoming && !nextIsScheduled && (
          <p className="portal-muted portal-next-hint">
            Next 2nd Tuesday after this one: {formatDate(data.next_scheduled_date)} —{' '}
            <Link to="/volunteer/meetings">schedule it</Link>.
          </p>
        )}
      </section>

      {/* ── Segments I own: rate footing, add photos ──────────────── */}
      <MyTrails />

      {/* ── My action items ──────────────────────────────────────── */}
      <section className="portal-section">
        <h2>Your Assignments</h2>
        {data.my_actions.length === 0 ? (
          <p className="portal-muted">Nothing assigned to you right now. 🎉</p>
        ) : (
          <div className="portal-action-list">
            {data.my_actions.map((a) => (
              <PortalActionItem key={a.id} action={a} onChanged={load} />
            ))}
          </div>
        )}
      </section>

      {/* ── Overdue across the committee ─────────────────────────── */}
      {data.overdue_actions.length > 0 && (
        <section className="portal-section">
          <h2>Overdue</h2>
          <div className="portal-action-list">
            {data.overdue_actions.map((a) => (
              <PortalActionItem key={a.id} action={a} onChanged={load} />
            ))}
          </div>
        </section>
      )}

      {/* ── Needs an owner ───────────────────────────────────────── */}
      {data.unassigned_actions.length > 0 && (
        <section className="portal-section">
          <h2>Needs an Owner</h2>
          <div className="portal-action-list">
            {data.unassigned_actions.map((a) => (
              <PortalActionItem key={a.id} action={a} onChanged={load} />
            ))}
          </div>
        </section>
      )}

      {/* ── Last meeting's minutes ───────────────────────────────── */}
      {last && (
        <section className="portal-section">
          <h2>Latest Minutes</h2>
          <div className="portal-meeting-card">
            <div className="portal-meeting-card-head">
              <div>
                <Link to={`/volunteer/meetings/${last.id}`} className="portal-meeting-title">{last.title}</Link>
                <div className="portal-meeting-when">{formatDate(last.meeting_date)}</div>
              </div>
              {!last.minutes_published_at && <span className="portal-badge badge-draft">Minutes unpublished</span>}
            </div>
            {last.minutes_published_at && last.minutes_md ? (
              <Markdown className="portal-minutes-preview">{last.minutes_md}</Markdown>
            ) : (
              <p className="portal-muted">
                Minutes not published yet.
                {canManage && <> <Link to={`/volunteer/meetings/${last.id}`}>Write them →</Link></>}
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
